import React, { useState, useMemo } from 'react';
import MainNavigation from '../../components/navigation/MainNavigation';
import GameFlowNavigation from '../../components/navigation/GameFlowNavigation';
import GemCard from './components/GemCard';
import ProgressionPanel from './components/ProgressionPanel';
import StatisticsPanel from './components/StatisticsPanel';
import FilterControls from './components/FilterControls';
import EmptyState from './components/EmptyState';

const GemCollection = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [activeSort, setActiveSort] = useState('date-desc');

  const mockGems = [
    {
      id: 1,
      name: "Cyber Shard",
      rarity: "legendary",
      image: "https://images.pexels.com/photos/1616403/pexels-photo-1616403.jpeg",
      imageAlt: "Glowing purple crystalline gem with electric blue lightning effects radiating from center against dark cyberpunk background",
      aiMood: "Chaos (Rage Pro)",
      difficultyTier: 5,
      dateAcquired: "01/15/2026"
    },
    {
      id: 2,
      name: "Neon Crystal",
      rarity: "epic",
      image: "https://images.pixabay.com/photo/2016/11/29/12/13/crystal-1869550_1280.jpg",
      imageAlt: "Bright cyan crystalline structure with sharp geometric edges glowing with neon light in dark environment",
      aiMood: "Confident (Pro Troll)",
      difficultyTier: 4,
      dateAcquired: "01/14/2026"
    },
    {
      id: 3,
      name: "Matrix Gem",
      rarity: "rare",
      image: "https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b",
      imageAlt: "Green glowing hexagonal gem with digital matrix code patterns flowing across surface in cyberpunk style",
      aiMood: "Aggressive",
      difficultyTier: 3,
      dateAcquired: "01/13/2026"
    },
    {
      id: 4,
      name: "Digital Diamond",
      rarity: "rare",
      image: "https://images.pexels.com/photos/1616403/pexels-photo-1616403.jpeg",
      imageAlt: "Transparent diamond-shaped crystal with holographic rainbow reflections and digital glitch effects on edges",
      aiMood: "Balanced",
      difficultyTier: 3,
      dateAcquired: "01/12/2026"
    },
    {
      id: 5,
      name: "Pixel Stone",
      rarity: "common",
      image: "https://images.pixabay.com/photo/2017/02/15/10/39/salzkristalle-2068540_1280.jpg",
      imageAlt: "Small cubic pixelated green crystal with 8-bit style edges and soft glow effect in retro gaming aesthetic",
      aiMood: "Passive",
      difficultyTier: 1,
      dateAcquired: "01/11/2026"
    },
    {
      id: 6,
      name: "Glitch Shard",
      rarity: "epic",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23",
      imageAlt: "Fragmented magenta crystal with digital distortion effects and glitching visual artifacts around sharp edges",
      aiMood: "Confident (Pro Troll)",
      difficultyTier: 4,
      dateAcquired: "01/10/2026"
    },
    {
      id: 7,
      name: "Byte Crystal",
      rarity: "common",
      image: "https://images.pexels.com/photos/1616403/pexels-photo-1616403.jpeg",
      imageAlt: "Simple cubic cyan crystal with clean geometric faces and subtle internal glow in minimalist design",
      aiMood: "Passive",
      difficultyTier: 1,
      dateAcquired: "01/09/2026"
    },
    {
      id: 8,
      name: "Quantum Gem",
      rarity: "legendary",
      image: "https://images.pixabay.com/photo/2016/11/29/12/13/crystal-1869550_1280.jpg",
      imageAlt: "Multifaceted golden crystal with swirling energy particles and quantum wave patterns emanating from core",
      aiMood: "Chaos (Rage Pro)",
      difficultyTier: 5,
      dateAcquired: "01/08/2026"
    },
    {
      id: 9,
      name: "Code Fragment",
      rarity: "rare",
      image: "https://images.unsplash.com/photo-1611080626919-7cf5a9dbab5b",
      imageAlt: "Angular blue crystal with visible binary code streams flowing through transparent crystalline structure",
      aiMood: "Aggressive",
      difficultyTier: 3,
      dateAcquired: "01/07/2026"
    },
    {
      id: 10,
      name: "Retro Jewel",
      rarity: "common",
      image: "https://images.pexels.com/photos/1616403/pexels-photo-1616403.jpeg",
      imageAlt: "Round green gemstone with pixelated surface texture and soft retro gaming console color palette",
      aiMood: "Balanced",
      difficultyTier: 2,
      dateAcquired: "01/06/2026"
    },
    {
      id: 11,
      name: "Holo Shard",
      rarity: "epic",
      image: "https://images.pixabay.com/photo/2017/02/15/10/39/salzkristalle-2068540_1280.jpg",
      imageAlt: "Prismatic crystal shard displaying holographic rainbow spectrum with futuristic light refraction patterns",
      aiMood: "Confident (Pro Troll)",
      difficultyTier: 4,
      dateAcquired: "01/05/2026"
    },
    {
      id: 12,
      name: "Synth Crystal",
      rarity: "rare",
      image: "https://images.unsplash.com/photo-1518709268805-4e9042af9f23",
      imageAlt: "Purple synthetic crystal with geometric facets and pulsing neon pink glow from internal light source",
      aiMood: "Aggressive",
      difficultyTier: 3,
      dateAcquired: "01/04/2026"
    }
  ];

  const progressionData = {
    currentTier: 4,
    winsRequired: 3,
    nextTierWins: 50,
    totalWins: 38,
    scalingFactor: 1.5
  };

  const statistics = {
    totalGems: mockGems?.length,
    rarityDistribution: {
      common: mockGems?.filter(g => g?.rarity === 'common')?.length,
      rare: mockGems?.filter(g => g?.rarity === 'rare')?.length,
      epic: mockGems?.filter(g => g?.rarity === 'epic')?.length,
      legendary: mockGems?.filter(g => g?.rarity === 'legendary')?.length
    },
    nextMilestone: {
      current: 12,
      target: 25,
      reward: "Chaos Mood Unlock"
    },
    collectionValue: 4850
  };

  const filteredAndSortedGems = useMemo(() => {
    let filtered = activeFilter === 'all' 
      ? mockGems 
      : mockGems?.filter(gem => gem?.rarity === activeFilter);

    const rarityOrder = { common: 1, rare: 2, epic: 3, legendary: 4 };

    return [...filtered]?.sort((a, b) => {
      switch (activeSort) {
        case 'date-desc':
          return new Date(b.dateAcquired) - new Date(a.dateAcquired);
        case 'date-asc':
          return new Date(a.dateAcquired) - new Date(b.dateAcquired);
        case 'rarity-desc':
          return rarityOrder?.[b?.rarity] - rarityOrder?.[a?.rarity];
        case 'rarity-asc':
          return rarityOrder?.[a?.rarity] - rarityOrder?.[b?.rarity];
        default:
          return 0;
      }
    });
  }, [activeFilter, activeSort]);

  return (
    <div className="gem-collection min-h-screen bg-background">
      <MainNavigation />
      <GameFlowNavigation />
      <main className="pt-16 pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-12">
          <div className="mb-6 md:mb-8 lg:mb-12">
            <h1 className="font-heading font-bold text-3xl md:text-4xl lg:text-5xl text-primary mb-2 md:mb-3 neon-text-cyan">
              Gem Collection
            </h1>
            <p className="text-sm md:text-base lg:text-lg text-muted-foreground font-caption max-w-3xl">
              Your trophy room showcasing earned gems and progression through escalating difficulty tiers. Each gem represents victories against AI opponents with unique moods and personalities.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mb-6 md:mb-8 lg:mb-12">
            <div className="lg:col-span-2">
              <ProgressionPanel progressionData={progressionData} />
            </div>
            <div>
              <StatisticsPanel statistics={statistics} />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 md:gap-6 lg:gap-8">
            <div className="lg:col-span-1">
              <FilterControls
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
                activeSort={activeSort}
                onSortChange={setActiveSort}
              />
            </div>

            <div className="lg:col-span-3">
              {filteredAndSortedGems?.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                  {filteredAndSortedGems?.map((gem, index) => (
                    <GemCard key={gem?.id} gem={gem} index={index} />
                  ))}
                </div>
              ) : (
                <div className="bg-card border-2 border-primary rounded-sm shadow-glow-cyan-md">
                  <EmptyState filterActive={activeFilter !== 'all'} />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default GemCollection;